'use client';

import Script from 'next/script';
import { SITE_CONFIG } from '@/constants';
import { getStoredFbclid, getStoredGclid } from '@/src/lib/clickIds';
import { PreferredSourcePlacement } from '@/src/types/preferredSource';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

function buildConfigScript(): string {
  const containerID = SITE_CONFIG.features.analytics.googleTagContainer;
  const ga4IDs = SITE_CONFIG.features.analytics.ga4MeasurementIds;
  const adsIDs = SITE_CONFIG.features.analytics.googleAds.ids;

  let configScript = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
  `;

  if (containerID) {
    configScript += `gtag('config', '${containerID}');`;
  }

  if (ga4IDs) {
    ga4IDs.split(',').map(id => id.trim()).filter(Boolean).forEach(ga4ID => {
      configScript += `gtag('config', '${ga4ID}');`;
    });
  }

  if (adsIDs) {
    adsIDs.split(',').map(id => id.trim()).filter(Boolean).forEach(adsID => {
      configScript += `gtag('config', '${adsID}');`;
    });
  }

  return configScript;
}

export default function GoogleAnalytics() {
  const containerID = SITE_CONFIG.features.analytics.googleTagContainer;
  const ga4IDs = SITE_CONFIG.features.analytics.ga4MeasurementIds;
  const adsIDs = SITE_CONFIG.features.analytics.googleAds.ids;

  if (!containerID && !ga4IDs && !adsIDs) {
    return null;
  }

  const primaryID = containerID || ga4IDs?.split(',')[0] || adsIDs?.split(',')[0];
  if (!primaryID) return null;

  return (
    <>
      <Script
        id="ga-gtag-js"
        src={`https://www.googletagmanager.com/gtag/js?id=${primaryID}`}
        strategy="lazyOnload"
      />
      <Script
        id="ga-gtag-config"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{ __html: buildConfigScript() }}
        onLoad={() => {
          window.gtag = function () {
            window.dataLayer = window.dataLayer || [];
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer.push(arguments);
          };
        }}
      />
    </>
  );
}

interface GoogleConsentCategories {
  analytics: boolean;
  marketing: boolean;
}

export function updateGoogleConsent(categories: GoogleConsentCategories) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const ads = categories.marketing ? 'granted' : 'denied';
  const analytics = categories.analytics ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: analytics,
  });
}

// Counts clicks only: Google sends nothing back when the visitor finishes on its page.
export function trackPreferredSourceClick(placement: PreferredSourcePlacement) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'preferred_source_button_click', { placement });
}

export enum GaEcommerceEvent {
  ViewItem = 'view_item',
  AddToCart = 'add_to_cart',
  ViewCart = 'view_cart',
  BeginCheckout = 'begin_checkout',
}

export interface GaEcommerceItem {
  item_id: string;
  item_name: string;
  // Face value + its currency ("50 USD"): the denomination is the variant.
  item_variant?: string;
  // Left out when the unit price in the event currency is not known yet.
  price?: number;
  quantity: number;
}

// GA4 recommended ecommerce funnel, pushed gtag-style exactly like the
// success page's purchase: GTM reads eventModel.{currency,value,items} and
// forwards the event to G-8VJHDMEGQ7; Consent Mode gates the hit. Browser-only
// by design - only purchase has a server copy.
export function trackGaEcommerce(event: GaEcommerceEvent, currency: string, value: number, items: GaEcommerceItem[]) {
  if (typeof window === 'undefined' || !window.gtag || items.length === 0) return;
  window.gtag('event', event, { currency, value, items });
}

export function getGaClientId(): string {
  if (typeof window === 'undefined') {
    return "";
  }

  const gaCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('_ga='));

  if (gaCookie) {
    const cookieValue = gaCookie.split('=')[1];
    const parts = cookieValue.split('.');
    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  }

  // No `_ga` cookie means no client id. There is deliberately no fallback to
  // the `_ga_<stream>` cookie: its value starts "GS1.1", which is a constant,
  // not an identifier - sending it would merge every such buyer into one GA4 user.
  return "";
}

// Session id of the CURRENTLY LOADED GA4 stream, read from its `_ga_<stream>`
// cookie and sent with the order so the backend's Measurement Protocol purchase
// joins the buyer's real session (otherwise it lands as Unassigned and paid
// channels lose their revenue).
//
// Never "the first _ga_ cookie": visitors from before the 2026-08 property
// consolidation still carry the retired stream's cookie, and browsers return
// cookies oldest-first, so the first match is reliably the wrong stream.
// Handles both cookie formats: GS1.1.<sessionId>.<n>… and GS2.1.s<sessionId>$o<n>…
export function getGaSessionId(): string {
  if (typeof window === 'undefined') {
    return "";
  }

  const streamCookies = document.cookie
    .split('; ')
    .filter(row => row.startsWith('_ga_'));

  if (streamCookies.length === 0) {
    return "";
  }

  const sessionIdOf = (cookie: string): string => {
    const parts = (cookie.split('=')[1] || '').split('.');
    if (parts.length < 3) return "";
    const raw = parts[2].startsWith('s') ? parts[2].slice(1) : parts[2];
    const sessionId = raw.split('$')[0];
    return /^\d+$/.test(sessionId) ? sessionId : "";
  };

  // The measurement id of the stream GTM actually loaded, e.g. 'G-8VJHDMEGQ7'
  // → cookie '_ga_8VJHDMEGQ7'.
  const loadedMeasurementId = Object.keys(
    (window as unknown as { google_tag_manager?: Record<string, unknown> }).google_tag_manager || {}
  ).find(key => key.startsWith('G-'));

  if (loadedMeasurementId) {
    const activeCookie = streamCookies.find(row =>
      row.startsWith(`_ga_${loadedMeasurementId.slice(2)}=`)
    );
    const activeSessionId = activeCookie ? sessionIdOf(activeCookie) : "";
    if (activeSessionId) return activeSessionId;
  }

  // Fallback when the tag object isn't reachable: newest session wins. Session
  // ids are unix timestamps and a retired stream's cookie stops being refreshed.
  return streamCookies
    .map(sessionIdOf)
    .filter(Boolean)
    .sort((a, b) => Number(b) - Number(a))[0] || "";
}

export function getGclid(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const gclidCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('_gcl_aw='));

  if (gclidCookie) {
    const cookieValue = gclidCookie.split('=')[1];
    const parts = cookieValue.split('.');
    if (parts.length >= 3) {
      return parts[2];
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  // Landing-URL stash: covers checkout on a page without ?gclid when the
  // consent-gated Conversion Linker never wrote _gcl_aw (see lib/clickIds.ts).
  return urlParams.get('gclid') || getStoredGclid();
}

export function getFbclid(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const fbclidCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('_fbc='));

  if (fbclidCookie) {
    const cookieValue = fbclidCookie.split('=')[1];
    const parts = cookieValue.split('.');
    if (parts.length >= 3) {
      return parts.slice(2).join('.');
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  // Same stash fallback as getGclid: _fbc only exists once the consent-gated
  // fbevents.js has loaded on a URL that still carried ?fbclid.
  return urlParams.get('fbclid') || getStoredFbclid();
}
